import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../../services/api'
import { useAuthStore } from '../../context/auth'
import { Badge, Card, EmptyState, Field, Input, Select, Spinner } from '../../components/ui'
import { Icon } from '../../components/ui/icons'
import { APPOINTMENT_STATUS_COLORS, APPOINTMENT_STATUS_LABELS, formatRut, formatTime } from '../../utils/format'
import type { DailyRoute, Professional } from '../../types/api'

/**
 * Ruta del día del profesional en terreno (H7): sus visitas a domicilio de
 * una fecha, en orden, con dirección, referencias y teléfono del paciente.
 *
 * Está pensada para abrirse en el teléfono antes de salir: cada parada tiene
 * enlace directo a llamar y a abrir la dirección en el mapa.
 */

function todayValue(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

/** Búsqueda de la dirección en OpenStreetMap (sin API key, ver DT-30). */
function mapUrl(address: string): string {
  return `https://www.openstreetmap.org/search?query=${encodeURIComponent(address)}`
}

export function RutaDelDiaPage() {
  const user = useAuthStore((s) => s.user)
  const [date, setDate] = useState(todayValue())
  const [professionalId, setProfessionalId] = useState('')

  const isDoctor = user?.role === 'DOCTOR'

  const professionals = useQuery({
    queryKey: ['professionals'],
    queryFn: async () => (await api.get<Professional[]>('/professionals')).data,
    enabled: !isDoctor,
  })

  const route = useQuery({
    queryKey: ['route', date, professionalId],
    queryFn: async () =>
      (await api.get<DailyRoute>('/appointments/route', { params: { date, ...(professionalId ? { professionalId } : {}) } })).data,
  })

  const stops = route.data?.stops ?? []

  return (
    <div className="space-y-4">
      <div>
        <h1 className="font-display text-2xl font-bold text-ink">Ruta del día</h1>
        <p className="text-sm text-steel-600">Visitas a domicilio en orden de hora, con dirección y contacto.</p>
      </div>

      <Card>
        <div className="flex flex-wrap items-end gap-3">
          <Field label="Fecha">
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </Field>
          {!isDoctor && (
            <Field label="Profesional">
              <Select value={professionalId} onChange={(e) => setProfessionalId(e.target.value)}>
                <option value="">Todos</option>
                {(professionals.data ?? []).map((p) => (
                  <option key={p.id} value={p.id}>{p.firstName} {p.lastName}</option>
                ))}
              </Select>
            </Field>
          )}
        </div>
      </Card>

      {route.isLoading ? (
        <Spinner />
      ) : stops.length === 0 ? (
        <EmptyState message="Sin visitas a domicilio esta fecha." />
      ) : (
        <ol className="space-y-3">
          {stops.map((stop, index) => (
            <li key={stop.id} className="rounded-2xl border border-steel-100 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex min-w-0 gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-600 font-display text-sm font-bold text-white">
                    {index + 1}
                  </span>
                  <div className="min-w-0">
                    <p className="font-display font-semibold text-ink">
                      {formatTime(stop.startsAt)} — {stop.patient.firstName} {stop.patient.lastName}
                    </p>
                    <p className="text-sm text-steel-600">
                      {formatRut(stop.patient.rut)} · {stop.service.name} ({stop.service.durationMin} min)
                    </p>
                    {!isDoctor && (
                      <p className="text-xs text-steel-500">
                        {stop.professional.firstName} {stop.professional.lastName}
                      </p>
                    )}
                  </div>
                </div>
                <Badge className={APPOINTMENT_STATUS_COLORS[stop.status]}>{APPOINTMENT_STATUS_LABELS[stop.status]}</Badge>
              </div>

              <div className="mt-3 space-y-1.5 border-t border-steel-100 pt-3 text-sm">
                <p className="flex items-start gap-2">
                  <Icon name="location" className="mt-0.5 h-4 w-4 shrink-0 text-brand-600" />
                  {stop.visitAddress ? (
                    <a href={mapUrl(stop.visitAddress)} target="_blank" rel="noopener noreferrer" className="text-brand-700 hover:underline">
                      {stop.visitAddress}
                    </a>
                  ) : (
                    <span className="text-amber-700">Sin dirección registrada</span>
                  )}
                </p>
                {stop.visitNotes && (
                  <p className="flex items-start gap-2 text-steel-600">
                    <Icon name="checkup" className="mt-0.5 h-4 w-4 shrink-0 text-steel-400" />
                    {stop.visitNotes}
                  </p>
                )}
                {stop.patient.phone && (
                  <p className="flex items-center gap-2">
                    <Icon name="mobile" className="h-4 w-4 shrink-0 text-brand-600" />
                    <a href={`tel:${stop.patient.phone}`} className="text-brand-700 hover:underline">{stop.patient.phone}</a>
                  </p>
                )}
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  )
}
